import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import {
  ArrowLeft, Plus, Trash2, MessageCircle, Sparkles, CornerDownLeft, Send, X,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { BusinessMultiSelect } from "../../components/vc/BusinessMultiSelect";
import { ImageStrip } from "../../components/vc/ImageStrip";
import { toast } from "sonner";
import { buildWhatsAppUrl } from "../../lib/phone";

const UNITS = ["pcs", "kg", "ltr", "box", "bag", "meter", "gram", "dozen"];

export default function VCReviewEnquiry() {
  const location = useLocation();
  const navigate = useNavigate();
  const { items: initItems = [], customerId: initCustomerId = "", source, image_data, images: initImages = [] } = location.state || {};
  const isImageMode = source === "image" || !!image_data || (initImages && initImages.length > 0);

  const [rows, setRows] = useState(() =>
    (initItems.length ? initItems : [{ product: "", quantity: 1, unit: "pcs" }]).map((it) => ({
      product: it.product || "",
      quantity: it.quantity || 1,
      unit: it.unit || "pcs",
      supplier_ids: [],
    }))
  );
  const [title, setTitle] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initItems.length === 0 && !image_data) {
      // no data — bounce back
      navigate("/enquiries/new", { replace: true });
      return;
    }
    apiClient.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  const update = (i, k, v) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  const remove = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const sameAsAbove = (i) => {
    if (i === 0) return;
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, supplier_ids: [...(r[i - 1].supplier_ids || [])] } : row)));
    toast.success("Copied businesses from above");
  };

  const addRow = () =>
    setRows((r) => [...r, { product: "", quantity: 1, unit: "pcs", supplier_ids: [] }]);

  const validate = () => {
    if (!title.trim()) {
      toast.error("Please enter an Enquiry Title before sending the enquiry.");
      return null;
    }
    // For image-only enquiries the product name is optional — the receiving
    // business will fill it in while quoting. Manual / Excel flows still
    // require a product name.
    const valid = rows.filter((r) => {
      const hasProduct = r.product.trim().length > 0;
      const productOk = isImageMode ? true : hasProduct;
      return productOk && Number(r.quantity) > 0 && (r.supplier_ids || []).length > 0;
    });
    if (valid.length === 0) {
      toast.error(isImageMode
        ? "Add at least one row with qty and business"
        : "Add at least one row with product, qty and business");
      return null;
    }
    return valid.map((r) => ({
      product: r.product.trim(),
      quantity: Number(r.quantity),
      unit: r.unit || "pcs",
      supplier_ids: r.supplier_ids,
    }));
  };

  const submit = async () => {
    const valid = validate();
    if (!valid) return;
    setSubmitting(true);
    try {
      // Multi-select per item — backend receives supplier_ids array per item
      const items = valid.map((v) => ({
        product: v.product,
        quantity: v.quantity,
        unit: v.unit,
        supplier_ids: v.supplier_ids,
      }));
      await apiClient.post("/bulk-price-requests", {
        title: title.trim(),
        items,
        image_data: image_data || (initImages && initImages[0]) || null,
        images: initImages || [],
      });
      toast.success("Enquiry sent — waiting for quotations");
      navigate("/enquiries");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to raise enquiry");
    } finally {
      setSubmitting(false);
    }
  };

  const submitWA = () => {
    const valid = validate();
    if (!valid) return;
    // Open wa.me for the first business of the first item — user can send further by hand
    const firstItem = valid[0];
    const firstSid = firstItem.supplier_ids[0];
    const sup = suppliers.find((s) => s.id === firstSid);
    const itemsForSup = valid.filter((r) => r.supplier_ids.includes(firstSid));
    const lines = itemsForSup
      .map((r, i) => `${i + 1}. ${r.product || "(see attached image)"} × ${r.quantity} ${r.unit}`)
      .join("\n");
    // Manual/temporary suppliers are NOT on KonexoCRM yet — send an invite-
    // style message so they can join and view the enquiry after connecting.
    const isManualBiz = !!sup && !sup.linked_owner_id;
    const appUrl = window.location.origin;
    const msg = isManualBiz
      ? (
          `Namaste ${sup?.name || "ji"} 🙏\n\n` +
          `You have a pending enquiry to respond to on KonexoCRM.\n\n` +
          `Requested items:\n${lines}\n\n` +
          `Download KonexoCRM and register using this mobile number. ` +
          `You'll see a pending connection request in the app — accept it to connect and view the enquiry.\n\n` +
          `Open KonexoCRM: ${appUrl}\n\n` +
          `Regards,\nKonexoCRM · Business Connected`
        )
      : `Namaste ${sup?.name || "ji"} 🙏\n\nPlease share your best rates for:\n\n${lines}\n\nRegards,\nKonexoCRM`;
    const url = buildWhatsAppUrl(sup?.phone, msg);
    if (!url) {
      toast.error("This business's phone number looks invalid — check it under Businesses before sharing.");
      return;
    }
    window.open(url, "_blank");
    submit();
  };

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-32 space-y-4">
        <button onClick={() => navigate(-1)} data-testid="review-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div>
          <div className="inline-flex items-center gap-1.5 text-emerald-600 text-[10px] font-bold uppercase tracking-widest mb-1">
            <Sparkles className="w-3 h-3" strokeWidth={2.6} /> Review
          </div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Review your enquiry</h2>
          <p className="text-sm text-slate-500 mt-0.5">Set a supplier for each item, then get quotes</p>
        </div>

        {(initImages && initImages.length > 0) ? (
          <div data-testid="review-image-preview" className="bg-white rounded-2xl border border-slate-100 p-2 space-y-1.5">
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 px-1">
              {initImages.length} image{initImages.length === 1 ? "" : "s"}
            </div>
            <ImageStrip images={initImages} testid="review-image-strip" />
          </div>
        ) : (image_data && (
          <div data-testid="review-image-preview" className="bg-white rounded-2xl border border-slate-100 p-2">
            <img src={image_data} alt="Enquiry" className="w-full rounded-xl object-cover max-h-56" />
          </div>
        ))}

        {/* Mandatory Enquiry Title — master identifier of the enquiry */}
        <div data-testid="review-title-block" className="bg-white rounded-2xl border border-slate-100 p-3 shadow-sm">
          <label htmlFor="review-title" className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-1">
            Enquiry Title <span className="text-red-500">*</span>
          </label>
          <Input
            id="review-title"
            data-testid="review-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. ABC Cement Plant, PO-2451, Site A, Tender-08"
            maxLength={140}
            className="h-11 rounded-xl mt-1"
          />
          <div className="text-[11px] text-slate-400 mt-1">
            Add a customer, project, PO or site name &mdash; you&rsquo;ll see this as the enquiry&rsquo;s identifier everywhere.
          </div>
        </div>

        <div className="space-y-2" data-testid="review-rows">
          {rows.map((row, i) => (
            <div
              key={i}
              data-testid={`review-row-${i}`}
              className="bg-white border border-slate-100 rounded-2xl p-2 shadow-sm space-y-1.5"
            >
              {/* ONE horizontal row: SN · Product · Qty · Business dropdown · Delete */}
              <div className="flex items-center gap-1.5">
                <span data-testid={`review-sn-${i}`} className="w-4 sm:w-5 shrink-0 text-center text-[11px] font-bold text-slate-400 tabular-nums">{i + 1}.</span>
                <Input
                  data-testid={`review-product-${i}`}
                  value={row.product}
                  onChange={(e) => update(i, "product", e.target.value)}
                  placeholder={isImageMode ? "Product (optional)" : "Product"}
                  className="h-10 rounded-lg flex-1 min-w-0 text-sm"
                />
                <Input
                  data-testid={`review-qty-${i}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={row.quantity}
                  onChange={(e) => update(i, "quantity", e.target.value)}
                  placeholder="Qty"
                  className="h-10 rounded-lg w-10 sm:w-12 text-sm text-right tabular-nums px-1.5"
                />
                <Select value={row.unit} onValueChange={(v) => update(i, "unit", v)}>
                  <SelectTrigger data-testid={`review-unit-${i}`} className="h-10 rounded-lg w-[54px] sm:w-[62px] text-sm px-1.5 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="min-w-[115px] flex-[2] shrink sm:min-w-0 sm:flex-1">
                  <BusinessMultiSelect
                    businesses={suppliers}
                    selectedIds={row.supplier_ids || []}
                    onChange={(next) => update(i, "supplier_ids", next)}
                    testidPrefix={`review-biz-${i}`}
                    placeholder="Business"
                    compact
                  />
                </div>
                <button
                  onClick={() => remove(i)}
                  data-testid={`review-delete-${i}`}
                  className="w-7 sm:w-8 h-10 shrink-0 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 grid place-items-center transition"
                  aria-label="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Compact secondary strip: Apply-to-All / Same-as-above + selected chips (only when relevant) */}
              {(
                (i === 0 && rows.length > 1) ||
                i > 0 ||
                (row.supplier_ids || []).length > 0
              ) && (
                <div className="flex items-center flex-wrap gap-1.5 pl-6">
                  {i === 0 && rows.length > 1 && (
                    <button
                      onClick={() => {
                        const first = rows[0]?.supplier_ids || [];
                        setRows((rs) => rs.map((r, idx) => idx === 0 ? r : { ...r, supplier_ids: [...first] }));
                        toast.success("Applied to all items");
                      }}
                      data-testid="review-apply-to-all"
                      className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 hover:text-emerald-800 normal-case shrink-0"
                    >
                      Apply to All
                    </button>
                  )}
                  {i > 0 && (
                    <button
                      onClick={() => sameAsAbove(i)}
                      data-testid={`review-same-as-above-${i}`}
                      className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 hover:text-indigo-700 flex items-center gap-1 normal-case shrink-0"
                    >
                      <CornerDownLeft className="w-3 h-3" /> Same as above
                    </button>
                  )}
                  {(row.supplier_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1" data-testid={`review-supplier-chips-${i}`}>
                      {(row.supplier_ids || []).map((sid) => {
                        const s = suppliers.find((x) => x.id === sid);
                        if (!s) return null;
                        return (
                          <span
                            key={sid}
                            data-testid={`review-chip-${i}-${sid}`}
                            className="inline-flex items-center gap-1 h-6 pl-2 pr-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold"
                          >
                            <span className="max-w-[100px] truncate">{s.name}</span>
                            <button
                              onClick={() => update(i, "supplier_ids", (row.supplier_ids || []).filter((x) => x !== sid))}
                              data-testid={`review-chip-remove-${i}-${sid}`}
                              className="w-4 h-4 rounded-full bg-white/20 hover:bg-white/30 grid place-items-center"
                              aria-label={`Remove ${s.name}`}
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {suppliers.length === 0 && (
            <div className="text-xs text-slate-500 bg-slate-50 rounded-xl p-2">No connected businesses yet.</div>
          )}
          <SecondaryButton onClick={addRow} data-testid="review-add-row" className="w-full">
            <Plus className="w-4 h-4 inline mr-1" /> Add Another Item
          </SecondaryButton>
        </div>

        {/* Sticky footer buttons */}
        {/* #1 — If the selected supplier(s) include manual/unregistered
             businesses, the buyer can only reach them via WhatsApp.
             Get Quote is hidden in that case; the WA button becomes full-width. */}
        {(() => {
          const selectedIds = new Set(rows.flatMap((r) => r.supplier_ids || []));
          const selected = suppliers.filter((s) => selectedIds.has(s.id));
          const anyManual = selected.some((s) => !s.linked_owner_id);
          if (anyManual) {
            return (
              <div className="pt-3">
                <SecondaryButton
                  onClick={submitWA}
                  disabled={submitting}
                  data-testid="review-wa-btn"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" /> Send Enquiry through WhatsApp
                </SecondaryButton>
                <p className="text-[11px] text-slate-500 text-center mt-2">
                  One or more selected businesses aren&apos;t on KonexoCRM yet — enquiry can be sent through WhatsApp only.
                </p>
              </div>
            );
          }
          return (
            <div className="grid grid-cols-2 gap-2 pt-3">
              <SecondaryButton
                onClick={submitWA}
                disabled={submitting}
                data-testid="review-wa-btn"
                className="flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" /> Via WhatsApp
              </SecondaryButton>
              <PrimaryButton
                onClick={submit}
                loading={submitting}
                data-testid="review-quote-btn"
                className="flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" /> Get Quote
              </PrimaryButton>
            </div>
          );
        })()}
      </div>
    </>
  );
}
